import React from 'react'
import { Tooltip } from 'react-tooltip'
import { DefaultErrorGroup, ErrorGroup, ErrorGroupState, IssueType } from '../../domain/Error'
import Link from '../../helper/link'

interface Props {
    navigator: any
    errorGroup: DefaultErrorGroup
    updateErrorState: (url: string) => void
    loading: boolean
}

interface StateLink {
    route: string
    routeArguments: any
    title: string
    buttonType: string
    tooltip: string
    issueTypes: string[]
    simpleLink?: boolean
}

function isNonIgnoreableExceptionType(exceptionType: string): boolean {
    return (
        exceptionType === 'Error' ||
        exceptionType === 'Exception' ||
        exceptionType === 'Error' ||
        exceptionType === 'TypeError' ||
        exceptionType === 'ClosedGeneratorException' ||
        exceptionType === 'ErrorException' ||
        exceptionType === 'ArgumentCountError' ||
        exceptionType === 'ArithmeticError' ||
        exceptionType === 'AssertionError' ||
        exceptionType === 'DivisionByZeroError' ||
        exceptionType === 'CompileError' ||
        exceptionType === 'ParseError' ||
        exceptionType === 'ValueError' ||
        exceptionType === 'UnhandledMatchError' ||
        exceptionType === 'FiberError' ||
        exceptionType === 'BadFunctionCallException' ||
        exceptionType === 'BadMethodCallException' ||
        exceptionType === 'DomainException' ||
        exceptionType === 'InvalidArgumentException' ||
        exceptionType === 'LengthException' ||
        exceptionType === 'LogicException' ||
        exceptionType === 'OutOfBoundsException' ||
        exceptionType === 'OutOfRangeException' ||
        exceptionType === 'OverflowException' ||
        exceptionType === 'RangeException' ||
        exceptionType === 'RuntimeException' ||
        exceptionType === 'UnderflowException' ||
        exceptionType === 'UnexpectedValueException'
    )
}

export class IssueWorkflowButtonGroup extends React.Component<Props> {
    render() {
        return (
            <>
                <div className="btn-group">
                    {this.possibleStatusChanges(this.props.errorGroup).map((link, key) =>
                        link.simpleLink ? (
                            <Link
                                key={key}
                                route={link.route}
                                params={link.routeArguments}
                                data-tooltip-id="errorWorkflowTooltips"
                                data-tooltip-content={link.tooltip}
                                className={'btn btn-' + link.buttonType}
                            >
                                {link.title}
                            </Link>
                        ) : (
                            <button
                                key={key}
                                onClick={this.props.updateErrorState.bind(null, this.props.navigator.path(link.route, link.routeArguments))}
                                className={'btn btn-' + link.buttonType}
                                data-tooltip-id="errorWorkflowTooltips"
                                data-tooltip-content={link.tooltip}
                                disabled={this.props.loading}
                            >
                                {link.title}
                            </button>
                        ),
                    )}
                </div>
                <Tooltip id="errorWorkflowTooltips" place="bottom-start" className="tw-react-tooltip" />
            </>
        )
    }

    private possibleStatusChanges(errorGroup: ErrorGroup): StateLink[] {
        const openAction: StateLink = {
            route: 'XhprofErrorBundle.Errors.changeStatus',
            routeArguments: { error: errorGroup.id, status: ErrorGroupState.OPEN },
            title: 'Re-Open',
            buttonType: 'default',
            tooltip: 'Reopen this issue',
            issueTypes: [IssueType.ERROR, IssueType.SLOWSQL, IssueType.NOTICE, IssueType.WARNING, IssueType.DEPRECATED],
        }

        const notifyToggleAction: StateLink = {
            route: 'XhprofErrorBundle.Errors.toggleNotify',
            routeArguments: { error: errorGroup.id },
            title: errorGroup.notify ? 'Mute' : 'Unmute',
            buttonType: 'default',
            tooltip: errorGroup.notify
                ? 'Do not send notifications when this issue occurs.'
                : 'Re-enable notifications when this issue occurs.',
            issueTypes: [IssueType.ERROR, IssueType.SLOWSQL],
        }

        const notErrorAction: StateLink = {
            route: 'XhprofErrorBundle.Errors.changeStatus',
            routeArguments: { error: errorGroup.id, status: ErrorGroupState.IGNORED },
            title: 'Ignore',
            buttonType: 'default',
            tooltip: 'Ignore occurrences of this error',
            issueTypes: [IssueType.ERROR, IssueType.SLOWSQL],
        }

        const incidentArchiveAction: StateLink = {
            route: 'XhprofAlertBundle.Incident.archive',
            routeArguments: { incidentId: errorGroup.id, s: this.props.navigator.current.query.s },
            title: 'Archive',
            buttonType: 'default',
            tooltip: 'Archive this incident.',
            issueTypes: [IssueType.INCIDENT],
        }

        const incidentConfigAction: StateLink = {
            route: 'XhprofAlertBundle.Configuration.editCheck',
            routeArguments: { checkId: errorGroup.checkId },
            title: 'Config',
            buttonType: 'default',
            tooltip: 'Configure this notification.',
            issueTypes: [IssueType.INCIDENT],
            simpleLink: true,
        }

        const links: StateLink[] = []

        if (errorGroup.status === ErrorGroupState.OPEN) {
            links.push({
                route: 'XhprofErrorBundle.Errors.changeStatus',
                routeArguments: { error: errorGroup.id, status: ErrorGroupState.RESOLVED },
                title: 'Resolve',
                buttonType: 'default',
                tooltip: 'Mark this issue as resolved across all environments. It will re-open automatically when it occurs again.',
                issueTypes: [IssueType.ERROR, IssueType.SLOWSQL, IssueType.NOTICE, IssueType.WARNING, IssueType.DEPRECATED],
            })

            links.push(notErrorAction)
            links.push(notifyToggleAction)
            links.push(incidentArchiveAction)
            links.push(incidentConfigAction)
        }

        if (errorGroup.status === ErrorGroupState.RESOLVED) {
            links.push(openAction)
            links.push(notErrorAction)
            links.push(notifyToggleAction)
            links.push(incidentConfigAction)
        }

        if (errorGroup.status === ErrorGroupState.IGNORED) {
            if (errorGroup.level === 'error') {
                if (!isNonIgnoreableExceptionType(errorGroup.exceptionType) && !errorGroup.isOnDefaultAlwaysIgnoreList) {
                    // Cannot mark error groups as "Always Ignore" when its a Fatal Error/Exception
                    links.push({
                        route: 'XhprofErrorBundle.Errors.ignore',
                        routeArguments: { error: errorGroup.id },
                        title: 'Always Ignore',
                        buttonType: 'danger',
                        tooltip: 'Ignore all issues of this type ' + errorGroup.exceptionType,
                        issueTypes: [IssueType.ERROR],
                    })
                } else if (errorGroup.isOnDefaultAlwaysIgnoreList) {
                    links.push({
                        route: 'XhprofErrorBundle.IgnoredExceptions.list',
                        routeArguments: {},
                        title: 'Configure',
                        buttonType: 'default',
                        tooltip: 'Review and configure always ignored exception settings.',
                        issueTypes: [IssueType.ERROR],
                        simpleLink: true,
                    })
                }
            }

            if (!errorGroup.isOnDefaultAlwaysIgnoreList) {
                links.push(openAction)
            }
        }

        if (errorGroup.status === ErrorGroupState.ALWAYS_IGNORED) {
            if (!errorGroup.isOnDefaultAlwaysIgnoreList) {
                links.push({
                    route: 'XhprofErrorBundle.Errors.unignore',
                    routeArguments: { error: errorGroup.id },
                    title: 'Unignore Always',
                    buttonType: 'default',
                    tooltip: 'Do not ignore all issues of this type anymore.',
                    issueTypes: [IssueType.ERROR],
                })
            }
            links.push(notifyToggleAction)
        }

        return links.filter((value: StateLink) => value.issueTypes.indexOf(errorGroup.level) >= 0)
    }
}
